import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

const MOODS = [
  { key: "happy", label: "Happy", color: "#E8B059" },
  { key: "calm", label: "Calm", color: "#8FA998" },
  { key: "anxious", label: "Anxious", color: "#B89C82" },
  { key: "sad", label: "Sad", color: "#7A8B99" },
  { key: "angry", label: "Angry", color: "#C27E67" },
];
const MOOD_VAL = { happy: 5, calm: 4, anxious: 3, sad: 2, angry: 1 };

export default function Mood() {
  const [mood, setMood] = useState("calm");
  const [intensity, setIntensity] = useState(5);
  const [note, setNote] = useState("");
  const [moods, setMoods] = useState([]);

  const load = () => api.get("/mood").then((r) => setMoods(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/mood", { mood, intensity, note });
      setNote("");
      toast.success("Mood logged.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const series = [...moods].reverse().slice(-30).map((m, i) => ({
    idx: i + 1, value: (MOOD_VAL[m.mood] || 3) * (m.intensity / 10), mood: m.mood,
  }));

  return (
    <div className="space-y-10">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">Mood Tracker</p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium">How are you, really?</h1>
      </div>

      <div className="bg-white rounded-3xl border border-border p-8 soft-shadow">
        <div className="flex flex-wrap gap-3 mb-6">
          {MOODS.map((m) => (
            <button key={m.key} onClick={() => setMood(m.key)}
                    data-testid={`mood-${m.key}`}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all border ${
                      mood === m.key ? "border-transparent text-white" : "border-border hover:bg-cream"
                    }`}
                    style={mood === m.key ? { backgroundColor: m.color } : {}}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="mb-4">
          <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Intensity · {intensity}/10</label>
          <input type="range" min="1" max="10" value={intensity}
                 onChange={(e) => setIntensity(parseInt(e.target.value))}
                 data-testid="mood-intensity"
                 className="w-full mt-3 accent-moss" />
        </div>
        <textarea placeholder="A note about what's happening (optional)..." value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid="mood-note"
                  className="w-full min-h-[100px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage" />
        <div className="flex justify-end mt-4">
          <button onClick={save} data-testid="mood-save"
                  className="px-6 py-2.5 rounded-full bg-moss text-white hover:bg-mossDark transition-all">
            Log mood
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow">
        <h3 className="font-serif text-xl mb-4">Recent mood wave</h3>
        {series.length === 0 ? (
          <p className="text-muted-foreground">Log a mood to see your weather pattern.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="moodG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C27E67" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#C27E67" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="idx" hide />
                <YAxis hide domain={[0, 5]} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #D8D4CC", background: "#fff" }}
                         formatter={(v, n, p) => [p.payload.mood, "Mood"]} labelFormatter={() => ""}/>
                <Area type="monotone" dataKey="value" stroke="#C27E67" strokeWidth={2.5} fill="url(#moodG)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-serif text-2xl mb-4">History</h3>
        <div className="space-y-2">
          {moods.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-border p-4 flex items-center gap-4">
              <div className="w-3 h-3 rounded-full" style={{ background: MOODS.find((x) => x.key === m.mood)?.color }} />
              <div className="flex-1">
                <p className="font-medium capitalize">{m.mood} <span className="text-muted-foreground text-sm">· {m.intensity}/10</span></p>
                {m.note && <p className="text-sm text-muted-foreground">{m.note}</p>}
              </div>
              <p className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
